import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getRawTextContent,
  getSelectionOffsetInElement,
} from '../SlideEditor.titleTextEditor.utils';
import {
  useFilteredVariableNames,
  VARIABLE_TRIGGER_REGEX,
} from '../SlideEditor.variableTypeahead.utils';
import {
  useVirtualSelectionAnchor,
  VariableTypeaheadPopper,
} from '../SlideEditor.variableTypeaheadMenu';

// The slide editor only renders one EditableHeader at a time, so
// querySelector returning the first match is always correct here.
const EDITABLE_HEADER_SELECTOR = '[data-testid="EditableHeader-input"]';

function getEditableElement(): HTMLElement | null {
  return document.querySelector(EDITABLE_HEADER_SELECTOR) as HTMLElement | null;
}

interface UseTitleVariableTypeaheadOptions {
  isEditing: boolean;
  resetKey: number;
  savedOffsetRef: React.MutableRefObject<number | null>;
  onChange: (newTitle: string) => void;
  setResetKey: React.Dispatch<React.SetStateAction<number>>;
}

interface UseTitleVariableTypeaheadResult {
  menuElement: React.ReactNode;
}

export function useTitleVariableTypeahead({
  isEditing,
  resetKey,
  savedOffsetRef,
  onChange,
  setResetKey,
}: UseTitleVariableTypeaheadOptions): UseTitleVariableTypeaheadResult {
  const [queryString, setQueryString] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const isOpen = queryString !== null;
  const filteredOptions = useFilteredVariableNames(queryString);

  // Close the menu when there are no matching options.
  const effectivelyOpen = isOpen && filteredOptions.length > 0;

  // Reset selected index when the query changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [queryString]);

  // Clamp selectedIndex so it's always valid for the current options list,
  // even before the useEffect above runs on the next render.
  const clampedIndex = Math.min(
    selectedIndex,
    Math.max(filteredOptions.length - 1, 0)
  );

  // ---- Refs for stable event handler closures ----
  const filteredOptionsRef = useRef(filteredOptions);
  filteredOptionsRef.current = filteredOptions;

  const selectedIndexRef = useRef(clampedIndex);
  selectedIndexRef.current = clampedIndex;

  const effectivelyOpenRef = useRef(effectivelyOpen);
  effectivelyOpenRef.current = effectivelyOpen;

  // ---- Selection handler ----
  const handleSelect = useCallback(
    (variableName: string) => {
      const cursorOffset = savedOffsetRef.current;
      if (cursorOffset === null) return;

      // Read the live text from the DOM rather than the title prop, because
      // EditableHeader only propagates edits to the parent on blur — any
      // text typed during the current session would be lost if we read the
      // stale prop value.
      const el = getEditableElement();
      if (!el) return;
      const currentTitle = getRawTextContent(el);

      // Re-derive the trigger match from the DOM text rather than relying on
      // queryStringRef, which updates asynchronously via React state. This
      // avoids a race where savedOffsetRef (updated synchronously from the
      // DOM selectionchange listener) is ahead of queryStringRef on fast
      // keystrokes.
      const textBeforeCursor = currentTitle.slice(0, cursorOffset);
      const triggerMatch = VARIABLE_TRIGGER_REGEX.exec(textBeforeCursor);
      if (!triggerMatch) return;

      const fullTrigger = triggerMatch[1] ?? '';
      const triggerStart = cursorOffset - fullTrigger.length;

      const charBefore = currentTitle[triggerStart - 1];
      const needsSpace =
        triggerStart > 0 &&
        charBefore !== ' ' &&
        charBefore !== '\u00a0' &&
        charBefore !== '(';

      const token = `{${variableName}}`;
      const newTitle =
        currentTitle.slice(0, triggerStart) +
        (needsSpace ? ' ' : '') +
        token +
        currentTitle.slice(cursorOffset);

      const newCursorPos = triggerStart + (needsSpace ? 1 : 0) + token.length;

      onChange(newTitle);
      savedOffsetRef.current = newCursorPos;
      setResetKey((k) => k + 1);
      setQueryString(null);
    },
    [onChange, savedOffsetRef, setResetKey]
  );

  const handleSelectRef = useRef(handleSelect);
  handleSelectRef.current = handleSelect;

  // ---- Trigger detection from DOM ----
  const runTriggerCheck = useCallback(() => {
    const offset = getSelectionOffsetInElement('EditableHeader-input');
    if (offset === null) {
      setQueryString(null);
      return;
    }
    const el = getEditableElement();
    if (!el) return;

    const fullText = getRawTextContent(el);
    const textBeforeCursor = fullText.slice(0, offset);
    const match = VARIABLE_TRIGGER_REGEX.exec(textBeforeCursor);
    if (match) {
      setQueryString(match[2] ?? '');
    } else {
      setQueryString(null);
    }
  }, []);

  // ---- DOM event listeners ----
  // Attach input + keydown listeners to the contenteditable element.
  // Re-attaches when the element remounts (resetKey) or editing state changes.
  useEffect(() => {
    if (!isEditing) {
      setQueryString(null);
      return;
    }

    const el = getEditableElement();
    if (!el) return;

    const handleInput = () => {
      // Use rAF to ensure the browser has updated the selection after input.
      requestAnimationFrame(() => {
        runTriggerCheck();
      });
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!effectivelyOpenRef.current) return;

      const opts = filteredOptionsRef.current;
      const idx = selectedIndexRef.current;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => {
          const len = filteredOptionsRef.current.length;
          return prev < len - 1 ? prev + 1 : 0;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => {
          const len = filteredOptionsRef.current.length;
          return prev > 0 ? prev - 1 : len - 1;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // stopPropagation in capture phase prevents EditableHeader's
        // bubble-phase onKeyDown from calling blur().
        e.stopPropagation();
        const option = opts[idx];
        if (option) {
          handleSelectRef.current(option);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setQueryString(null);
      } else if (e.key === 'Tab') {
        setQueryString(null);
      }
    };

    // Capture phase so we intercept before EditableHeader's bubble handler.
    el.addEventListener('keydown', handleKeyDown, true);
    el.addEventListener('input', handleInput);

    return () => {
      el.removeEventListener('keydown', handleKeyDown, true);
      el.removeEventListener('input', handleInput);
    };
    // Re-attach when the element remounts (resetKey changes) or editing toggles.
    // Refs are used for all other dependencies so we don't need them here.
  }, [isEditing, resetKey, runTriggerCheck]);

  // ---- Re-check trigger on selection change (cursor movement without input) ----
  useEffect(() => {
    if (!isEditing || !effectivelyOpen) return;

    const handleSelectionChange = () => {
      runTriggerCheck();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing, effectivelyOpen, runTriggerCheck]);

  // ---- Popper positioning ----
  const virtualAnchor = useVirtualSelectionAnchor();

  // ---- Scroll highlighted item into view ----
  useEffect(() => {
    if (!effectivelyOpen) return;
    const el = document.getElementById(`title-typeahead-item-${clampedIndex}`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [clampedIndex, effectivelyOpen]);

  // ---- Menu element ----
  const menuElement = effectivelyOpen ? (
    <VariableTypeaheadPopper
      anchorEl={virtualAnchor}
      options={filteredOptions}
      selectedIndex={clampedIndex}
      onSelect={handleSelect}
      onHighlight={setSelectedIndex}
      idPrefix="title-typeahead-item"
      preventFocusLoss
    />
  ) : null;

  return { menuElement };
}
